import { useEffect, useState } from 'react'
import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button/Button'
import { Input } from 'almanac-ui/TextInputField/Input'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { ArrowLeft, Check2, Trash2 } from 'blaze-ui/icons/20'
import { ChevronLeft, ChevronRight } from 'blaze-ui/icons/24'
import Styles from './TopicEditDrawer.module.scss'

/**
 * Returns the current keyboard height in pixels to offset the drawer from the keyboard
 * Need this for safari on mobile
 */
function useKeyboardOffset() {
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const updateOffset = () => {
      const diff = window.innerHeight - vv.height
      setOffset(diff > 0 ? diff : 0)
    }

    const pinScroll = () => {
      if (window.scrollY !== 0 || window.scrollX !== 0) {
        window.scrollTo(0, 0)
      }
    }

    vv.addEventListener('resize', updateOffset)
    vv.addEventListener('scroll', updateOffset)
    window.addEventListener('scroll', pinScroll)
    return () => {
      vv.removeEventListener('resize', updateOffset)
      vv.removeEventListener('scroll', updateOffset)
      window.removeEventListener('scroll', pinScroll)
    }
  }, [])

  return offset
}

interface TopicEditDrawerProps extends DrawerProps {
  initialIndex: number
  topics: string[]
  minValue: number
  isNew?: boolean
  onSave: (index: number, value: string) => void
  onAdd?: (value: string) => void
  onDelete: (index: number) => void
}

export const TopicEditDrawer = observer(
  ({ initialIndex, topics, minValue, isNew, onSave, onAdd, onDelete, ...props }: TopicEditDrawerProps) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex)
    const [localValue, setLocalValue] = useState(isNew ? '' : topics[initialIndex] || '')
    const { drawersStore } = useRootStore()
    const keyboardOffset = useKeyboardOffset()

    const hasPrev = !isNew && currentIndex > 0
    const hasNext = !isNew && currentIndex < topics.length - 1

    const navigateTo = (nextIndex: number) => {
      const trimmed = localValue.trim()
      if (trimmed !== topics[currentIndex]) {
        onSave(currentIndex, trimmed)
      }
      setCurrentIndex(nextIndex)
      setLocalValue(topics[nextIndex] || '')
    }

    return (
      <Drawer.Root {...props} activeSnapPoint={1} snapPoints={[1]}>
        <Drawer.Overlay />
        <Drawer.Portal>
          <Drawer.Content fullHeight>
            <div style={{ height: '100%' }} data-vaul-no-drag>
              <Drawer.InnerContent isSnappedTop>
                <Drawer.Top>
                  <Drawer.Back onPress={() => drawersStore.closeDrawer()} frontIcon={ArrowLeft}>
                    {''}
                  </Drawer.Back>
                  <Drawer.Title asChild>
                    <Heading lineClamp={1} level={5}>
                      {isNew ? 'Add topic' : 'Edit topic'}
                    </Heading>
                  </Drawer.Title>
                  <Drawer.Action
                    onPress={() => {
                      const trimmed = localValue.trim()
                      if (isNew) {
                        if (trimmed.length > 0) onAdd?.(trimmed)
                      } else {
                        onSave(currentIndex, trimmed)
                      }
                      drawersStore.closeDrawer()
                    }}
                    frontIcon={Check2}
                    position="right"
                  >
                    Save
                  </Drawer.Action>
                  <Drawer.Description />
                </Drawer.Top>
                <Drawer.ScrollableContainer snap={1}>
                  <div className={Styles.container}>
                    <div className={Styles.editorContainer}>
                      <Input
                        isTextarea
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        name="topic-edit"
                        size="xl"
                        key={currentIndex}
                        autoFocus
                      />
                    </div>
                    {!isNew && topics.length > minValue && (
                      <div className={Styles.deleteContainer}>
                        <Button
                          variant="secondary"
                          size="md"
                          frontIcon={Trash2}
                          onPress={() => {
                            onDelete(currentIndex)
                            drawersStore.closeDrawer()
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                </Drawer.ScrollableContainer>
                {!isNew && (
                  <div
                    className={Styles.navContainer}
                    style={keyboardOffset > 0 ? { bottom: keyboardOffset + 10 } : undefined}
                  >
                    <button
                      type="button"
                      className={Styles.navButton}
                      onClick={() => hasPrev && navigateTo(currentIndex - 1)}
                      disabled={!hasPrev}
                    >
                      <ChevronLeft size={24} />
                      <span className={Styles.navLabel}>Prev</span>
                    </button>
                    <button
                      type="button"
                      className={Styles.navButton}
                      onClick={() => hasNext && navigateTo(currentIndex + 1)}
                      disabled={!hasNext}
                    >
                      <ChevronRight size={24} />
                      <span className={Styles.navLabel}>Next</span>
                    </button>
                  </div>
                )}
              </Drawer.InnerContent>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    )
  },
)
